import cn from "@/v2-ui/utils/utils.cn"
import Checkbox from "@/v2-ui/checkbox"
import Label from "@/v2-ui/label"
import Select from "@/v2-ui/select"
import CountryPicker from "@/v2-backoffice-client/country/CountryPicker"
import ProjectOption from "@/v2-backoffice-client/project/ProjectOption"
import { useProjectByTenantQuery } from "@/v2-backoffice-client/project/byTenant/project.byTenant.query"
import { useTenantDefaultsQuery } from "@/v2-backoffice-client/tenant/defaults/tenant.defaults.query"
import type { ProjectByTenantQuery } from "@/v2-backoffice-client/project/byTenant/__types__/project.byTenant.query"

// Radix Select forbids an empty-string item value, so "let the team's project
// set decide" carries this sentinel and maps back to null on the way out.
const AUTOMATIC_PROJECT_VALUE = "auto"

type TenantProject = ProjectByTenantQuery["getProjectsByTenantId"][number]

export type InvitationCreateProjectFieldsProps = {
  className?: string
  tenantId: number
  // BigInt project id as a string (codegen shape); null = automatic.
  projectId: string | null
  onProjectIdChange(value: string | null): void
  autoCreateProject: boolean
  onAutoCreateProjectChange(value: boolean): void
  countryCode: number | null
  onCountryCodeChange(value: number | null): void
  // The word for the people this batch mints — "invited" (default) in the
  // invite dialogs, "created" in direct user creation. Same fields, same
  // backend rules; only the sentence under the control changes.
  subject?: "invited" | "created"
}

// Picks the project invited users start in on first login
// (Configuration.ActiveProjectId). The team's own project set normally
// decides — see describeOutcome for the rules, which the backend applies and
// this component states in plain words — so both controls here are overrides:
// naming a project wins over the rules, and the checkbox opts a project-less
// team out of getting one created.
function InvitationCreateProjectFields(
  props: InvitationCreateProjectFieldsProps,
) {
  const {
    className,
    tenantId,
    projectId,
    onProjectIdChange,
    autoCreateProject,
    onAutoCreateProjectChange,
    countryCode,
    onCountryCodeChange,
    subject = "invited",
  } = props

  const { data, loading } = useProjectByTenantQuery({
    variables: { tenantId },
  })
  const { data: tenantData, loading: tenantLoading } = useTenantDefaultsQuery({
    variables: { id: tenantId },
  })

  const projects = data?.getProjectsByTenantId ?? []
  const canCreate = projectId === null && projects.length === 0

  // The team's stated default only counts while it still resolves to one of
  // these projects — the same test the backend applies, so a default pointing
  // at a deleted or unlinked project reads here as no default at all.
  const defaultProject = projects.find(
    (project) => project.Id === tenantData?.getTenantById?.DefaultProjectId,
  )
  const pending = loading || tenantLoading
  const outcome = resolveAutomatic({
    projects,
    autoCreateProject,
    defaultProject,
  })

  // Same treatment for the country's own "automatic": name the fallback the
  // backend will reach for, rather than leaving the operator to guess.
  const automaticCountryLabel = tenantData?.getTenantById?.DefaultCountryCode
    ? "Automatic (team's default)"
    : "Automatic (Norway)"

  return (
    <div className={cn("invitation-create-form-project", className)}>
      <span className="invitation-create-form-label">Default project</span>
      <Select
        value={projectId ?? AUTOMATIC_PROJECT_VALUE}
        onValueChange={(next) => {
          onProjectIdChange(next === AUTOMATIC_PROJECT_VALUE ? null : next)
        }}
      >
        <Select.Trigger
          size="md"
          className="min-w-[240px]"
          title={`The project the ${subject} users start in on first login. Leave on Automatic unless this batch must land somewhere specific.`}
        >
          <Select.Value
            placeholder={pending ? "Loading projects..." : "Automatic"}
          />
        </Select.Trigger>
        <Select.Content>
          <Select.Item value={AUTOMATIC_PROJECT_VALUE}>
            {pending ? "Automatic" : describeAutomatic(outcome)}
          </Select.Item>
          {projects.map((project) => (
            <Select.Item key={project.Id} value={project.Id}>
              <ProjectOption
                name={project.Name}
                countryCode={project.CountryCode}
                createdAt={project.CreatedAt}
              />
            </Select.Item>
          ))}
        </Select.Content>
      </Select>

      {!pending && (
        <span className="invitation-create-form-project-hint">
          {describeOutcome(projectId, outcome, subject)}
        </span>
      )}

      {!pending && canCreate && (
        <>
          <Label className="invitation-create-form-autocreate">
            <Checkbox
              checked={autoCreateProject}
              onCheckedChange={(checked) => {
                onAutoCreateProjectChange(checked === true)
              }}
            />
            Create a project for this team
          </Label>

          {/* Only a project being created needs a country: an existing one
              already has its own, and this can't change it. */}
          {autoCreateProject && (
            <CountryPicker
              value={countryCode}
              onValueChange={onCountryCodeChange}
              emptyLabel={automaticCountryLabel}
              placeholder={automaticCountryLabel}
              title="The country the new project's data is scoped to. Automatic uses the team's default country, then Norway."
            />
          )}
        </>
      )}
    </div>
  )
}

type AutomaticOutcome =
  | { kind: "default"; project: TenantProject }
  | { kind: "only"; project: TenantProject }
  | { kind: "create" }
  | { kind: "none" }
  | { kind: "userPicks"; count: number }

type ResolveAutomaticInput = {
  projects: TenantProject[]
  autoCreateProject: boolean
  defaultProject: TenantProject | undefined
}

// What "Automatic" resolves to for this team: the backend's rules for an
// invite that names no project, in its order — see resolveInvitationProjects.
// Both things the operator reads are formatted from this one resolution, so
// the option label and the sentence under it can never drift apart.
function resolveAutomatic(input: ResolveAutomaticInput): AutomaticOutcome {
  const { projects, autoCreateProject, defaultProject } = input

  if (defaultProject) return { kind: "default", project: defaultProject }
  if (projects.length === 0) {
    return autoCreateProject ? { kind: "create" } : { kind: "none" }
  }
  if (projects.length === 1) return { kind: "only", project: projects[0] }
  return { kind: "userPicks", count: projects.length }
}

// The option label. "Automatic" on its own asks the operator to know the
// rules by heart, so it says what it will do to this particular team.
function describeAutomatic(outcome: AutomaticOutcome) {
  switch (outcome.kind) {
    case "default":
      return `Automatic (team default: ${outcome.project.Name})`
    case "only":
      return `Automatic (only project: ${outcome.project.Name})`
    case "create":
      return "Automatic (creates a project)"
    case "none":
      return "Automatic (no project)"
    case "userPicks":
      return "Automatic (users pick on first login)"
  }
}

// The sentence under the control: the same outcome with the reasoning the
// short label has no room for.
function describeOutcome(
  projectId: string | null,
  outcome: AutomaticOutcome,
  subject: "invited" | "created",
) {
  if (projectId !== null) {
    return `Every ${subject} user starts in this project.`
  }

  switch (outcome.kind) {
    case "default":
      return `This team's default project (${outcome.project.Name}) becomes every ${subject} user's start project.`
    case "only":
      return `This team's only project (${outcome.project.Name}) becomes every ${subject} user's start project.`
    case "create":
      return `This team has no projects, so one is created and every ${subject} user starts there.`
    case "none":
      return `This team has no projects, so the ${subject} users start without one.`
    case "userPicks":
      return `This team has ${outcome.count} projects and no default, so the ${subject} users pick one themselves on first login.`
  }
}

export default InvitationCreateProjectFields
